import React, { useRef, useState, useLayoutEffect } from 'react';
import { Icon } from '../utils/icons';

// Chapter ribbon: a single SVG whose path is recomputed in real pixels whenever the
// label width changes (ResizeObserver). The tail — diagonal depth + corner radius —
// is fixed in px, so only the straight top/bottom grows with the name; the diagonal
// never stretches. (Technique from the reference mock; H is measured so the ribbon
// keeps the card's own badge height instead of a hard-coded one.)
const TAIL_DEPTH = 14; // horizontal depth of the diagonal — constant
const RADIUS = 7;      // rounded outer corner — constant

const ChapterRibbon = ({ name, thumbnail, icon }) => {
    const contentRef = useRef(null);
    const [shape, setShape] = useState({ w: 0, h: 0, d: '' });
    const [iconOk, setIconOk] = useState(false);

    // The icon is the thumbnail painted as a mask filled with --ribbon-text, so it's
    // the exact ribbon color. A mask can't signal a load failure (it would show a solid
    // block), so preload the image and only render the icon once it's known to load.
    useLayoutEffect(() => {
        if (!thumbnail) { setIconOk(false); return; }
        let alive = true;
        const img = new Image();
        img.onload = () => { if (alive) setIconOk(true); };
        img.onerror = () => { if (alive) setIconOk(false); };
        img.src = thumbnail;
        return () => { alive = false; };
    }, [thumbnail]);

    useLayoutEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        const draw = () => {
            const W = el.offsetWidth;
            const H = el.offsetHeight;
            const vx = W - TAIL_DEPTH;          // diagonal vertex, before rounding
            const len = Math.hypot(TAIL_DEPTH, H) || 1;
            const p1x = vx + (TAIL_DEPTH / len) * RADIUS; // curve start, on the diagonal
            const p1y = H - (H / len) * RADIUS;
            const p2x = vx - RADIUS;            // curve end, onto the flat bottom

            setShape({
                w: W,
                h: H,
                d: `M0 0 L${W} 0 L${p1x.toFixed(2)} ${p1y.toFixed(2)} `
                    + `Q${vx.toFixed(2)} ${H} ${p2x.toFixed(2)} ${H} L0 ${H} Z`,
            });
        };

        draw();
        const ro = new ResizeObserver(draw);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <div className="agenda-card__chapter">
            <svg
                className="agenda-card__chapter-shape"
                width={shape.w}
                height={shape.h}
                viewBox={`0 0 ${shape.w} ${shape.h}`}
                aria-hidden="true"
            >
                <path d={shape.d} />
            </svg>
            <span className="agenda-card__chapter-content" ref={contentRef}>
                {iconOk && (
                    <span
                        className="agenda-card__chapter-icon"
                        style={{ WebkitMaskImage: `url("${thumbnail}")`, maskImage: `url("${thumbnail}")` }}
                        aria-hidden="true"
                    />
                )}
                {!thumbnail && icon && (
                    <span className="agenda-card__chapter-glyph" aria-hidden="true">
                        <Icon name={icon} size={15} strokeWidth={1.5} />
                    </span>
                )}
                <span className="agenda-card__chapter-name" title={name}>{name}</span>
            </span>
        </div>
    );
};

export default ChapterRibbon;
